import type { UseQueryResult } from "@tanstack/react-query";
import type { Read } from "@/components/ui/box.schema";
import { formatInCourseZone } from "@/lib/session-portal";
import type { RegradeJudgement, RegradeQuestion, RegradeView } from "@/lib/api/regrades";

const DAY_TIME: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" };
/** An exact local date and time in the course's zone, never a relative phrase. */
export const exactly = (instant: string, timezone: string) => formatInCourseZone(instant, timezone, DAY_TIME);
const figure = (value: number) => value.toLocaleString("en-US", { maximumFractionDigits: 2 });

export function resultWords(judgement: RegradeJudgement | null | undefined, points: number | null): string {
  if (!judgement || judgement.score == null) return "No score";
  return points != null ? `${figure(judgement.score)} of ${figure(points)}` : figure(judgement.score);
}

/** A question as the paper names it: its number and prompt, never its stored key. */
export function questionLabel(view: Pick<RegradeView, "questions">, key: string): string {
  const index = view.questions.findIndex(question => question.id === key);
  const question: RegradeQuestion | undefined = view.questions[index];
  return question ? `Question ${index + 1}: ${question.prompt}` : "A question no longer on the paper";
}

export const SAID = "whitespace-pre-wrap border-s-2 border-rule ps-3 text-body text-foreground [overflow-wrap:anywhere]";

/** A query as the kit reads it: loading, failed with Retry, stale with its last value, or ready. Never an absent box (R1, R2). */
export function queryRead<T>(query: Pick<UseQueryResult<T>, "data" | "dataUpdatedAt" | "isError" | "isRefetchError" | "refetch">, sentence: string): Read<T> {
  if (query.data !== undefined) {
    const readAt = new Date(query.dataUpdatedAt || Date.now()).toISOString();
    return query.isRefetchError ? { status: "stale", last: query.data, readAt, sentence: "Couldn't refresh. A later change isn't shown.", refresh: () => void query.refetch() }
      : { status: "ready", value: query.data, readAt };
  }
  return query.isError ? { status: "failed", sentence, retry: () => void query.refetch() } : { status: "loading" };
}

/** A ready or stale read's value; nothing otherwise. */
export const readValue = <T,>(read: Read<T>): T | undefined => read.status === "ready" ? read.value : read.status === "stale" ? read.last : undefined;

/** While a quiz replacement holds the result, the request and its deadline stand and nothing of a decision is read (C1). */
export const HELD = "Your result is held while your replacement attempt is open; the regrade deadline does not pause or move.";

export const outcomeWords = (outcome: "unchanged" | "changed", previous: RegradeJudgement | null | undefined, result: RegradeJudgement | null | undefined, points: number | null) =>
  outcome === "changed" ? `Result changed from ${resultWords(previous, points)} to ${resultWords(result, points)}` : `Result unchanged at ${resultWords(previous, points)}`;

/** A request record in one phrase: not requested, closed, held, awaiting or released. */
export function recordWords(view: RegradeView): string {
  if (!view.request) return view.state === "closed" ? "Window closed" : "Not requested";
  if (view.held) return "Result held";
  return view.request.status === "pending" ? "Awaiting your instructor's decision" : "Decision released";
}
